import React, { useCallback, useMemo } from 'react';
import { Bubble, Button, Dropdown, List } from 'tea-component';
import { DBO_TM } from '@op/utils/format';
import chaiAiSvg from '@op/assets/svg/ai-chat.svg';
import CommonBubble from '@home/components/CommonBubble';
import useLanguage from '@op/hooks/useLanguage';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { CONTACT_EMAIL, CONTACT_NAME } from '@op/constants';
import './index.scss';
import { PromptModalType } from '../../hooks/usePromptModal';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useAgentStoreContext, useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import SvgIcon from '@home/components/SvgIcon';
import TextBubbles from '@home/components/TextBubbles';

export interface IStatsHeaderBarProps {
  showStatsHeader?: boolean;
  origSummary: any;
  feedsState: any;
  filter: any;
  wholeGameInfo: any;
  promptInfo?: PromptModalType;
}

type TCopilotFileItem = { type: 'file'; file: { mimeType: string; uri: string; }; } | { type: 'sql'; text: string; };

const StatsHeaderBar: React.FC<IStatsHeaderBarProps> = ({
  showStatsHeader,
  origSummary,
  feedsState,
  filter,
  wholeGameInfo,
  promptInfo
}) => {
  const { formatMessage, isEn } = useLanguage();
  const { openCopilotWithDefaultAgent, isHaveAIPermission } = useCopilotOpenBridge();
  const { chatList } = useChatList({ manual: false });
  const { setItem } = useAgentStoreContext();
  const [, setIsShowCopilot] = useLocalState('__dba_show_copilot__', false);

  const historyChat = useMemo(() => {
    return chatList?.filter(item => {
      const { metadata } = item || {};
      return metadata?.quoted_content?.id === promptInfo?.taskId;
    }) || [];
  }, [chatList, promptInfo]);

  const handleOpenCopilot = useCallback((): void => {
    if (!origSummary || typeof origSummary === 'string') return;
    const { files, sql } = origSummary;
    if (!files) return;
    const { taskId } = promptInfo || {};
    const excelUrl = isEn ? files.summary_excel_url_en || files.summary_excel_url_zh : files.summary_excel_url_zh || files.summary_excel_url_en;
    const mdUrl = isEn ? files.summary_md_url_en || files.summary_md_url_zh : files.summary_md_url_zh || files.summary_md_url_en;
    const filesList: TCopilotFileItem[] = [
      excelUrl ? { type: 'file', file: { mimeType: 'document', uri: excelUrl } } : null,
      mdUrl ? { type: 'file', file: { mimeType: 'document', uri: mdUrl } } : null,
      sql ? { type: 'sql', text: sql } : null,
    ].filter(Boolean) as TCopilotFileItem[];
    openCopilotWithDefaultAgent({
      prefixTag: `${wholeGameInfo?.entity_name ?? ''} - Custom AI Summary`,
      form: 'opinion_pc',
      files: filesList,
      prefixImage: wholeGameInfo?.cover ?? '',
      quoted_content: {
        id: taskId || '',
      }
    });
  }, [origSummary, isEn, wholeGameInfo, openCopilotWithDefaultAgent, promptInfo]);

  const handleSelectHistoryChat = useCallback((selectedSessionId: string) => {
    setItem('__dba_chat_copilot__', selectedSessionId);
    setIsShowCopilot(true);
  }, [setItem, setIsShowCopilot]);

  const chartTipsElement = useMemo(() => {
    const {
      POS_RATIO,
      NEG_RATIO,
      NEU_RATIO,
      TOTAL_NUM,
      TOTAL_POS,
      TOTAL_NEG,
      TOTAL_NEU,
    } = feedsState || {};
    if (!filter) return null;
    return (
      <div className="chart-common-formatter">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">
            {DBO_TM.timeTitle(filter)}
          </div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table className="undefined">
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                {formatMessage({ id: 'Mentions' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {TOTAL_NUM}
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#69D19C' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Positive' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {TOTAL_POS} ({POS_RATIO})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#7E92A1' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Neutral' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {TOTAL_NEU} ({NEU_RATIO})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#F17C86' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Negative' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {TOTAL_NEG} ({NEG_RATIO})
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }, [feedsState, formatMessage, filter]);

  if (!showStatsHeader) return null;
  if (!origSummary || typeof origSummary === 'string') return null;
  const { task_status, files } = origSummary;
  if (task_status !== 'success') return null;

  const oldReport = Object.keys(files).length === 0;
  const hasMdUrl = !!(files?.summary_md_url_zh || files?.summary_md_url_en);
  const {
    POS_RATIO,
    NEG_RATIO,
    NEU_RATIO,
    TOTAL_NUM,
    TOTAL_ENGAGEMENT,
  } = feedsState || {};

  return (
    <div className="dbo-aigc-card-header-bar">
      <div className="dbo-aigc-card-header-bar__left">
        <div className="dbo-aigc-card-header-bar__detail">
          {wholeGameInfo?.entity_name && (
            <span className="dbo-aigc-card-header-bar__game-name">
              {wholeGameInfo.entity_name}
            </span>
          )}
          <span className="dbo-aigc-card-header-bar__stats">
            {TOTAL_NUM} {formatMessage({ id: 'Mentions' })}
            <span className="dbo-aigc-card-header-bar__dot">·</span>
            {TOTAL_ENGAGEMENT} {formatMessage({ id: 'Engagement' })}
          </span>
        </div>
        <Bubble
          className="dbo-noborer-wrap"
          closeOnScroll
          content={chartTipsElement}
          dark
          placement="right"
        >
          <div className="dbo-aigc-card-mentions-opinions-bar">
            <div
              className="dbo-aigc-card-mentions-opinions-bar-item"
              style={{
                width: `${POS_RATIO}`,
                backgroundColor: '#69D19C',
              }}
            ></div>
            <div
              className="dbo-aigc-card-mentions-opinions-bar-item"
              style={{
                width: `${NEU_RATIO}`,
                backgroundColor: '#6E7794',
              }}
            ></div>
            <div
              className="dbo-aigc-card-mentions-opinions-bar-item"
              style={{
                width: `${NEG_RATIO}`,
                backgroundColor: '#F17C86',
              }}
            ></div>
          </div>
        </Bubble>
      </div>

      <div className="dbo-aigc-card-header-bar__right">
        {(isHaveAIPermission && !oldReport) && (
          <CommonBubble
            content={
              hasMdUrl
                ? undefined
                : formatMessage(
                  {
                    id: 'Failed to retrieve the files required for AI Chat; please contact {name} ({email}).',
                  },
                  { name: CONTACT_NAME, email: CONTACT_EMAIL },
                )
            }
          >
            <Button
              className="dbo-aigc-card-header-bar__ai-chat-btn"
              onClick={() => handleOpenCopilot()}
              disabled={!hasMdUrl}
            >
              <img src={chaiAiSvg} className="dbo-ai-chat-btn-icon" alt="AI Chat" />
              <span>{formatMessage({ id: 'AI Chat' })}</span>
            </Button>
          </CommonBubble>
        )}
        {
          historyChat?.length > 0 && (
            <Dropdown
              button={
                <Button
                  className="dbo-aigc-card-header-bar__ai-chat-history-btn"
                  disabled={!hasMdUrl}
                >
                  <SvgIcon type="updating" className="dbo-prompt-history-icon" />
                  <span>{formatMessage({ id: 'AI Chat History' })}</span>
                </Button>
              }
              appearance="pure"
              placement="bottom-end"
              clickClose
            >
              <List className="dbo-aigc-card-header-bar__history-list">
                {historyChat.map(chat => (
                  <List.Item
                    key={chat.session_id}
                    onClick={() => handleSelectHistoryChat(chat.session_id)}
                    className="dbo-aigc-card-header-bar__history-item"
                  >
                    <TextBubbles text={chat.title} />
                    <span className="dbo-aigc-card-header-bar__history-item-time">
                      {formatMessage({ id: 'Create time: {time}' }, { time: chat.timestamp })}
                    </span>
                  </List.Item>
                ))}
              </List>
            </Dropdown>
          )
        }
      </div>
    </div>
  );
};

export default StatsHeaderBar;
